import { useCallback, useEffect, useMemo, useRef, useState, type CSSProperties } from "react";
import clsx from "clsx";
import { Link, useParams } from "react-router-dom";
import {
  CheckCircle2,
  ChevronsRight,
  CircleDashed,
  Hash,
  Image as ImageIcon,
  MapPin,
  RotateCcw,
  SlidersHorizontal,
  Timer,
  Upload,
} from "lucide-react";
import AppShell from "@/components/AppShell";
import MapPreview, { type BoundaryInput, type MapPreviewHandle } from "@/components/MapPreview";
import RasterPanel from "@/components/RasterPanel";
import FeatureTable from "@/components/FeatureTable";
import PropertyImportPanel from "@/components/PropertyImportPanel";
import EditableLayersPanel from "@/components/EditableLayersPanel";
import FiltersLayersPanel, { type LayerCounts } from "@/components/workspace/FiltersLayersPanel";
import SummaryCards, { type SummaryCard } from "@/components/workspace/SummaryCards";
import FieldDataDrawer, { type FieldDataDrawerHandle } from "@/components/workspace/FieldDataDrawer";
import ManageUploadsDialog from "@/components/workspace/ManageUploadsDialog";
import QaResultsCard from "@/components/workspace/QaResultsCard";
import { Banner, Button } from "@/components/ui";
import {
  getEditableLayerPositions,
  getFeatureQa,
  getProject,
  listBlocksInZone,
  listFeatures,
  listRasters,
  listWards,
  listZones,
  type FeatureQa,
} from "@/lib/api";
import { getStoredUser } from "@/lib/auth";
import { useLanguage } from "@/i18n";
import { useEditableLayers } from "@/lib/useEditableLayers";
import { useMapView } from "@/lib/useMapView";
import { formatNumber } from "@/lib/format";
import { isRevisitActive } from "@/lib/revisit";
import {
  EMPTY_HIERARCHY,
  NO_FILTER,
  blocksFor,
  filterFeatures,
  reconcile,
  wardsFor,
  type Hierarchy,
  type HierarchyFilter,
} from "@/lib/hierarchyFilter";
import type { BlockStatus, FieldFeature, Project, Raster } from "@/lib/types";

const POLL_INTERVAL_MS = 5000;

/** West/south/east/north box around some GeoJSON geometries, or null if none has coordinates. */
function boundsOf(geometries: BoundaryInput["geometry"][]): [number, number, number, number] | null {
  let minLng = Infinity;
  let minLat = Infinity;
  let maxLng = -Infinity;
  let maxLat = -Infinity;
  const walk = (c: unknown): void => {
    if (!Array.isArray(c)) return;
    if (typeof c[0] === "number" && typeof c[1] === "number") {
      minLng = Math.min(minLng, c[0]);
      maxLng = Math.max(maxLng, c[0]);
      minLat = Math.min(minLat, c[1]);
      maxLat = Math.max(maxLat, c[1]);
      return;
    }
    c.forEach(walk);
  };
  geometries.forEach((g) => walk((g as { coordinates?: unknown }).coordinates));
  return Number.isFinite(minLng) ? [minLng, minLat, maxLng, maxLat] : null;
}

export default function ProjectWorkspacePage() {
  // react-router hands params straight off the URL, synchronously -- no
  // Promise to unwrap the way Next 15+ required via use(). Always a string,
  // so it is parsed once here now that projects use integer primary keys;
  // a non-numeric URL yields NaN and the fetches below simply return nothing.
  const { projectId: projectIdParam = "" } = useParams<{ projectId: string }>();
  const projectId = Number(projectIdParam);
  const { t, lang } = useLanguage();

  const [project, setProject] = useState<Project | null>(null);
  const [rasters, setRasters] = useState<Raster[]>([]);
  const [features, setFeatures] = useState<FieldFeature[]>([]);
  const [boundaries, setBoundaries] = useState<BoundaryInput[]>([]);
  /**
   * The same zones/wards/blocks the boundaries above are built from, kept
   * whole.
   *
   * BoundaryInput deliberately flattens the three levels into one list and
   * drops the parent links, which is right for drawing them and useless for
   * filtering: working out which zone a feature sits in is a block -> ward ->
   * zone walk. Kept from the fetch that was already happening rather than
   * asked for again.
   */
  const [hierarchy, setHierarchy] = useState<Hierarchy>(EMPTY_HIERARCHY);
  const [filter, setFilter] = useState<HierarchyFilter>(NO_FILTER);
  // Bumped after a property import so the map and the field table refetch.
  const [propertyKey, setPropertyKey] = useState(0);
  const [error, setError] = useState<string | null>(null);
  /** The field-data row last zoomed to, held so it stays outlined on the map
   *  and marked in the table until another is picked. */
  const [zoomedFeatureId, setZoomedFeatureId] = useState<number | null>(null);
  /** The upload/management dialog (imagery, property outlines, editable layers). */
  const [uploadsOpen, setUploadsOpen] = useState(false);
  /** The Filters & Layers side panel: open on wide screens, closed on phones. */
  const [panelOpen, setPanelOpen] = useState(
    () => typeof window === "undefined" || window.innerWidth >= 768
  );
  /** Whether the Field data drawer is open (the summary cards make way). */
  const [tableOpen, setTableOpen] = useState(false);
  /** What the map shows -- driven by the side panel. */
  const mapView = useMapView();
  const mapRef = useRef<MapPreviewHandle>(null);
  const drawerRef = useRef<FieldDataDrawerHandle>(null);
  /** Zooms the map after the table has made room for it (see revealMap). */
  const afterReveal = (zoom: () => void) => {
    const wait = drawerRef.current?.revealMap() ?? 0;
    if (wait > 0) window.setTimeout(zoom, wait);
    else zoom();
  };
  /** Overlap & sliver check: on/off, its result, and whether its list is open. */
  const [qaEnabled, setQaEnabled] = useState(false);
  const [qa, setQa] = useState<FeatureQa | null>(null);
  const [qaLoading, setQaLoading] = useState(false);
  const [qaError, setQaError] = useState<string | null>(null);
  const [qaListOpen, setQaListOpen] = useState(false);

  const isStaff = (getStoredUser()?.role ?? "surveyor") !== "surveyor";

  const editable = useEditableLayers(projectId);

  /** The layer shape last zoomed to from a layer tab. */
  const [zoomedLayerShape, setZoomedLayerShape] = useState<{ layerId: number; position: number } | null>(null);
  const zoomToLayerShape = async (layerId: number, position: number) => {
    setZoomedLayerShape({ layerId, position });
    // Switched on if hidden, so the shape is actually there to look at.
    if (!editable.visible[layerId]) editable.toggleVisible(layerId);
    const collection = await editable.loadLayer(layerId);
    const features = collection?.features ?? [];
    // A partial layer's features carry their position as `id`; a whole
    // layer's sit at their position.
    const feature =
      features.find((f) => (f as { id?: unknown }).id === position) ??
      (features[position] && typeof (features[position] as { id?: unknown }).id !== "number"
        ? features[position]
        : undefined);
    if (feature?.geometry) {
      const geometry = feature.geometry;
      afterReveal(() => mapRef.current?.zoomToGeometry(geometry));
    }
  };

  // With a zone, ward or block picked, the map draws only the editable-layer
  // shapes inside it (centre inside, as the layer tabs list them). The layers
  // stay loaded whole; the server answers which positions are in the area.
  const filterActive = filter.zoneId != null || filter.wardId != null || filter.blockId != null;
  const filterKey = `${filter.zoneId}|${filter.wardId}|${filter.blockId}`;
  /** The filter bar's "Selected only" switch: the map shows only the picked area. */
  const [selectedOnly, setSelectedOnly] = useState(true);
  const [layerAreas, setLayerAreas] = useState<{ key: string; positions: Record<number, Set<number>> }>({
    key: "",
    positions: {},
  });
  const drawnLayerKey = editable.drawn.map((l) => l.id).join(",");
  const layerStamp = (editable.layers ?? []).map((l) => `${l.id}:${l.updated_at}`).join(",");
  useEffect(() => {
    if (!filterActive) return;
    let cancelled = false;
    const ids = drawnLayerKey ? drawnLayerKey.split(",").map(Number) : [];
    Promise.all(
      ids.map((id) =>
        getEditableLayerPositions(projectId, id, filter)
          .then((p) => [id, new Set(p)] as const)
          .catch(() => [id, null] as const)
      )
    ).then((pairs) => {
      if (cancelled) return;
      const positions: Record<number, Set<number>> = {};
      for (const [id, set] of pairs) if (set) positions[id] = set;
      setLayerAreas({ key: filterKey, positions });
    });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId, filterActive, filterKey, drawnLayerKey, layerStamp]);
  const drawnLayers = useMemo(() => {
    if (!filterActive || !selectedOnly) return editable.drawn;
    return editable.drawn.map((l) => {
      const keep = layerAreas.key === filterKey ? layerAreas.positions[l.id] : undefined;
      return {
        ...l,
        collection: {
          ...l.collection,
          // Nothing until the area's positions arrive, rather than the whole
          // layer flashing up first. A partial layer's features carry their
          // position as `id`; a whole layer's are in position order.
          // Kept shapes carry their position as `id`, since the filter
          // changes their index (the map's highlight matches on it).
          features: keep
            ? l.collection.features.flatMap((f, i) => {
                const id = (f as { id?: unknown }).id;
                const pos = typeof id === "number" ? id : i;
                return keep.has(pos) ? [{ ...f, id: pos }] : [];
              })
            : [],
        },
      };
    });
  }, [editable.drawn, filterActive, selectedOnly, filterKey, layerAreas]);

  const refreshRasters = useCallback(() => {
    if (!isStaff) return;
    listRasters({ projectId })
      .then(setRasters)
      .catch((err) => setError(String(err)));
  }, [projectId, isStaff]);

  const refreshFeatures = useCallback(() => {
    listFeatures({ projectId })
      .then(setFeatures)
      .catch((err) => setError(String(err)));
  }, [projectId]);

  useEffect(() => {
    getProject(projectId).then(setProject).catch((err) => setError(String(err)));
    refreshRasters();
    refreshFeatures();

    // The whole zone/ward/block hierarchy, not just zones -- the map's own
    // Boundaries dropdown now gives each level its own toggle, so there's a
    // way to turn ward and block outlines off again for a project too big to
    // want all three at once. Cancellable: without `cancelled`, opening one
    // project then quickly navigating to another let the FIRST project's
    // slower fetch resolve last and overwrite the second project's boundaries
    // on screen with the wrong campaign's outlines.
    //
    // One call per zone, then one per ward -- N+1, deliberately. This project
    // has 3 zones and 36 wards; the real cost is HTTP round trips, not the
    // rows themselves, and that is the same trade HierarchyPage's own
    // per-block shape-count effect already makes at a comparable scale. A
    // scope-restricted account (see services/project_access.py) sees only its
    // own zones/wards/blocks here, same as everywhere else -- this page reads
    // the same endpoints and applies no filtering of its own.
    let cancelled = false;
    (async () => {
      try {
        const zones = await listZones(projectId);
        const out: BoundaryInput[] = zones
          .filter((z) => z.boundary)
          .map((z) => ({ id: z.id, label: z.name, geometry: z.boundary!, kind: "zone" as const }));

        const wardLists = await Promise.all(zones.map((z) => listWards(z.id)));
        const wards = wardLists.flat();

        // Per zone, not per ward: one request for a zone's blocks instead of
        // one per ward (25 for this project, ~5 s of round trips).
        const blockLists = await Promise.all(zones.map((z) => listBlocksInZone(z.id)));
        const blocks = blockLists.flat();

        // A ward has no status column of its own, so the map's progress
        // highlight gets one derived from the blocks inside it: started as
        // soon as any block is, done only once EVERY block is submitted.
        // Deliberately not "mostly done" -- a ward with one untouched block
        // still needs someone to go out, and colouring it as finished is the
        // one reading that would send nobody.
        const wardStatus = new Map<number, BlockStatus>();
        for (const w of wards) {
          const own = blocks.filter((b) => b.ward_id === w.id);
          if (own.length === 0) continue;
          if (own.every((b) => b.status === "submitted")) wardStatus.set(w.id, "submitted");
          else if (own.some((b) => b.status !== "pending")) wardStatus.set(w.id, "in_progress");
          else wardStatus.set(w.id, "pending");
        }

        for (const w of wards) {
          if (w.boundary) {
            out.push({
              id: w.id,
              label: w.name,
              geometry: w.boundary,
              kind: "ward",
              status: wardStatus.get(w.id),
            });
          }
        }

        for (const b of blocks) {
          if (b.boundary) {
            out.push({
              id: b.id,
              label: b.name,
              geometry: b.boundary,
              kind: "block",
              status: b.status,
            });
          }
        }

        if (!cancelled) {
          setBoundaries(out);
          // Every zone and ward, including the ones with no geometry that `out`
          // skips: a block's shapes are still filterable by a ward whose own
          // outline was never imported.
          setHierarchy({ zones, wards, blocks });
        }
      } catch {
        if (!cancelled) {
          setBoundaries([]);
          setHierarchy(EMPTY_HIERARCHY);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [projectId, refreshRasters, refreshFeatures, propertyKey]);

  // Poll only while something is still converting.
  useEffect(() => {
    const pending = rasters.some((r) => r.status === "uploaded" || r.status === "processing");
    if (!pending) return;
    const t = setInterval(refreshRasters, POLL_INTERVAL_MS);
    return () => clearInterval(t);
  }, [rasters, refreshRasters]);

  const readyCount = useMemo(() => rasters.filter((r) => r.status === "ready").length, [rasters]);

  const visibleFeatures = useMemo(
    () => filterFeatures(features, hierarchy, filter),
    [features, hierarchy, filter]
  );

  // "Selected only": while a zone / ward / block is picked, the map draws just
  // that area -- its own outline, the outlines inside it, its field data and
  // editable-layer shapes -- until the filter is cleared or the switch is off.
  // A picked block keeps its ward's outline around it for context.
  const isolate = selectedOnly && filterActive;
  const mapBoundaries = useMemo(() => {
    if (!isolate) return boundaries;
    const wardIds = new Set(
      filter.blockId !== null
        ? hierarchy.blocks.filter((b) => b.id === filter.blockId).map((b) => b.ward_id)
        : filter.wardId !== null
          ? [filter.wardId]
          : wardsFor(hierarchy, filter.zoneId).map((w) => w.id)
    );
    const blockIds = new Set(
      filter.blockId !== null
        ? [filter.blockId]
        : blocksFor(hierarchy, filter.zoneId, filter.wardId).map((b) => b.id)
    );
    return boundaries.filter((b) =>
      b.kind === "zone"
        ? b.id === filter.zoneId && filter.wardId === null && filter.blockId === null
        : b.kind === "ward"
          ? wardIds.has(b.id)
          : blockIds.has(b.id)
    );
  }, [isolate, boundaries, hierarchy, filter]);
  const mapFeatures = isolate ? visibleFeatures : features;

  // A filter set against one project must not survive into another, and a
  // hierarchy that reloads under a filter can drop the very block it names
  // (a deleted ward, a re-imported hierarchy). Re-checked here rather than
  // only in the dropdowns' own handler, which never runs in either case.
  useEffect(() => {
    setFilter((current) => {
      const next = reconcile(hierarchy, current);
      return next.zoneId === current.zoneId &&
        next.wardId === current.wardId &&
        next.blockId === current.blockId
        ? current
        : next;
    });
  }, [hierarchy]);

  useEffect(() => {
    setFilter(NO_FILTER);
  }, [projectId]);

  // Picking a zone, ward or block in the filter bar frames the map on it, so the
  // table and the map point at the same place. The narrowest level chosen wins;
  // if that level has no outline imported, fall back to the one above it.
  // Clearing the filter frames the whole project again.
  const lastFilterRef = useRef<HierarchyFilter>(NO_FILTER);
  useEffect(() => {
    const prev = lastFilterRef.current;
    lastFilterRef.current = filter;
    if (
      prev.zoneId === filter.zoneId &&
      prev.wardId === filter.wardId &&
      prev.blockId === filter.blockId
    ) {
      return; // not a filter change (e.g. the boundaries just finished loading)
    }
    const levels: [BoundaryInput["kind"], number | null][] = [
      ["block", filter.blockId],
      ["ward", filter.wardId],
      ["zone", filter.zoneId],
    ];
    if (levels.every(([, id]) => id === null)) {
      if (boundaries.length === 0) return;
      const zonesOnly = boundaries.filter((b) => b.kind === "zone");
      const box = boundsOf((zonesOnly.length ? zonesOnly : boundaries).map((b) => b.geometry));
      if (box) mapRef.current?.flyToBounds(box);
      return;
    }
    for (const [kind, id] of levels) {
      if (id === null) continue;
      const outline = boundaries.find((b) => b.kind === kind && b.id === id);
      if (outline) {
        mapRef.current?.zoomToGeometry(outline.geometry);
        return;
      }
    }
  }, [filter, boundaries]);

  // Run the overlap & sliver check while it is switched on, for whatever the
  // filter bar is showing. Re-runs when the filter or the data changes.
  useEffect(() => {
    if (!qaEnabled) {
      setQa(null);
      setQaError(null);
      return;
    }
    let cancelled = false;
    setQaLoading(true);
    setQaError(null);
    getFeatureQa(projectId, {
      zoneId: filter.zoneId ?? undefined,
      wardId: filter.wardId ?? undefined,
      blockId: filter.blockId ?? undefined,
    })
      .then((r) => !cancelled && setQa(r))
      .catch(() => !cancelled && setQaError(t.projectWorkspacePage.qaFailed))
      .finally(() => !cancelled && setQaLoading(false));
    return () => {
      cancelled = true;
    };
  }, [qaEnabled, projectId, filter, features, t]);

  const qaIssues = qa ? qa.overlap_count + qa.sliver_count : 0;

  // ── numbers for the summary cards and the layer panel ──────────────────────
  const blocksInView = useMemo(() => {
    if (filter.blockId !== null) return hierarchy.blocks.filter((b) => b.id === filter.blockId);
    return blocksFor(hierarchy, filter.zoneId, filter.wardId);
  }, [hierarchy, filter]);
  const blockStates = useMemo(() => {
    const out = { pending: 0, in_progress: 0, submitted: 0 };
    for (const b of blocksInView) {
      const s = (b.status ?? "pending") as keyof typeof out;
      if (s in out) out[s] += 1;
    }
    return out;
  }, [blocksInView]);
  const numberedInView = useMemo(
    () => visibleFeatures.filter((f) => (f.property_no ?? "").trim() !== "").length,
    [visibleFeatures]
  );
  const revisitsInView = useMemo(
    () => visibleFeatures.filter((f) => isRevisitActive(f.properties)).length,
    [visibleFeatures]
  );
  const layerCounts: LayerCounts = useMemo(() => {
    const c = { zone: 0, ward: 0, block: 0 };
    const p = { pending: 0, in_progress: 0, submitted: 0 };
    for (const b of mapBoundaries) {
      c[b.kind] += 1;
      if (b.kind === "block") {
        const s = (b.status ?? "pending") as keyof typeof p;
        if (s in p) p[s] += 1;
      }
    }
    return {
      ...c,
      imageryReady: readyCount,
      imageryTotal: rasters.length,
      features: mapFeatures.length,
      numbered: mapFeatures.filter((f) => (f.property_no ?? "").trim() !== "").length,
      pending: p.pending,
      inProgress: p.in_progress,
      submitted: p.submitted,
    };
  }, [mapBoundaries, mapFeatures, readyCount, rasters.length]);

  const fmt = (n: number) => formatNumber(n, lang);
  const w = t.workspace;
  const cards: SummaryCard[] = [
    {
      key: "properties",
      label: w.cardProperties,
      value: fmt(visibleFeatures.length),
      sub: filterActive ? w.ofTotal(fmt(features.length)) : undefined,
      icon: <MapPin className="h-4 w-4" />,
      color: "#FF6D00",
    },
    {
      key: "numbered",
      label: w.cardNumbered,
      value: fmt(numberedInView),
      icon: <Hash className="h-4 w-4" />,
      color: "#0096C7",
    },
    {
      key: "submitted",
      label: w.cardSubmitted,
      value: fmt(blockStates.submitted),
      sub: w.ofBlocks(fmt(blocksInView.length)),
      icon: <CheckCircle2 className="h-4 w-4" />,
      color: "#16A34A",
    },
    {
      key: "progress",
      label: w.cardInProgress,
      value: fmt(blockStates.in_progress),
      icon: <Timer className="h-4 w-4" />,
      color: "#F97316",
    },
    {
      key: "pending",
      label: w.cardNotStarted,
      value: fmt(blockStates.pending),
      icon: <CircleDashed className="h-4 w-4" />,
      color: "#8A8574",
    },
    ...(isStaff
      ? [
          {
            key: "imagery",
            label: w.cardImagery,
            value: fmt(readyCount),
            sub: w.ofTotal(fmt(rasters.length)),
            icon: <ImageIcon className="h-4 w-4" />,
            color: "#023E8A",
          },
        ]
      : []),
    {
      key: "revisit",
      label: w.cardRevisit,
      value: fmt(revisitsInView),
      icon: <RotateCcw className="h-4 w-4" />,
      color: "#0077B6",
    },
  ];

  // Manage uploads / Survey timing / Zones, wards & blocks: in the top bar on
  // wide screens, in a small row above the cards on narrow ones.
  const pageActions = isStaff ? (
    <>
              <Button variant="secondary" size="sm" onClick={() => setUploadsOpen(true)}>
                <Upload className="mr-1.5 h-3.5 w-3.5" /> {w.manageUploads}
              </Button>
              <Link to={`/projects/${projectId}/timing`}>
                <Button variant="secondary" size="sm">
                  {t.projectWorkspacePage.surveyTiming}
                </Button>
              </Link>
              <Link to={`/projects/${projectId}/hierarchy`}>
                <Button variant="primary" size="sm">
                  {t.projectWorkspacePage.zonesWardsBlocks}
                </Button>
              </Link>
            </>
  ) : null;

  return (
    <AppShell
      crumbs={[{ label: t.sidebar.projects, href: "/" }, { label: project?.name ?? "…" }]}
      actions={pageActions}
    >
      {/* A map-first dashboard filling the space under the top bar: summary
          cards, then the Filters & Layers panel beside the map, with the field
          data table as a drawer over the bottom of the map area. */}
      <div className="flex h-full min-h-[560px] flex-col gap-2 p-2">
        {error && (
          <Banner tone="danger" action={<Button size="sm" onClick={() => setError(null)}>{t.common.dismiss}</Button>}>
            {error}
          </Banner>
        )}

        {pageActions && <div className="flex flex-none flex-wrap gap-2 lg:hidden">{pageActions}</div>}

        {/* Tucked away while the table is open, so the table gets that room
            too; back as soon as it closes. */}
        <div
          className={clsx(
            "flex-none overflow-hidden transition-[max-height,opacity] duration-300 ease-smooth",
            tableOpen ? "max-h-0 opacity-0" : "max-h-32 opacity-100"
          )}
          aria-hidden={tableOpen}
        >
          <SummaryCards cards={cards} />
        </div>

        <div className="reveal relative flex min-h-0 flex-1 overflow-hidden rounded-md border border-line bg-surface shadow-lift" style={{ "--i": 2 } as CSSProperties}>
          {/* Filters & Layers. Beside the map on wide screens; over it on small ones. */}
          {panelOpen ? (
            <div className="absolute inset-y-0 left-0 z-20 w-[300px] max-w-[85%] flex-none border-r border-line shadow-xl md:static md:z-auto md:shadow-none">
              <FiltersLayersPanel
                onCollapse={() => setPanelOpen(false)}
                hierarchy={hierarchy}
                filter={filter}
                onFilterChange={setFilter}
                shown={visibleFeatures.length}
                total={features.length}
                selectedOnly={selectedOnly}
                onSelectedOnlyChange={setSelectedOnly}
                mapView={mapView}
                counts={layerCounts}
                editable={editable}
                onZoomToLayer={(bbox) => mapRef.current?.flyToBounds(bbox)}
                qa={
                  isStaff
                    ? {
                        enabled: qaEnabled,
                        onToggle: () => setQaEnabled((on) => !on),
                        loading: qaLoading,
                        overlaps: qa?.overlap_count,
                        slivers: qa?.sliver_count,
                      }
                    : undefined
                }
                onManageUploads={isStaff ? () => setUploadsOpen(true) : undefined}
              />
            </div>
          ) : (
            <button
              type="button"
              onClick={() => setPanelOpen(true)}
              title={w.openPanel}
              className="flex w-11 flex-none flex-col items-center gap-3 border-r border-line bg-surface py-3 text-ink-muted transition hover:bg-surface-sunken hover:text-ink"
            >
              <ChevronsRight className="h-4 w-4" />
              <SlidersHorizontal className="h-4 w-4" />
              <span className="text-2xs font-bold uppercase tracking-wider [writing-mode:vertical-rl]">
                {w.filtersAndLayers}
              </span>
            </button>
          )}

          <div className="flex min-w-0 flex-1 flex-col">
            <div className="relative min-h-0 flex-1">
              <MapPreview
                ref={mapRef}
                rasters={rasters}
                features={mapFeatures}
                boundaries={mapBoundaries}
                selectedFeatureId={zoomedFeatureId}
                selectedLayerShape={zoomedLayerShape}
                editableLayers={drawnLayers}
                qaEnabled={qaEnabled}
                qa={qa}
                view={mapView.view}
              />

              {/* Overlap & sliver result, floating over the top of the map. */}
              {isStaff && qaEnabled && (
                <div className="absolute left-1/2 top-3 z-10 w-[min(92%,640px)] -translate-x-1/2">
                  <QaResultsCard
                    qa={qa}
                    loading={qaLoading}
                    error={qaError}
                    listOpen={qaListOpen}
                    onToggleList={() => setQaListOpen((o) => !o)}
                    onClose={() => setQaEnabled(false)}
                    onZoom={(geometry, featureId) => {
                      if (featureId != null) setZoomedFeatureId(featureId);
                      mapRef.current?.zoomToGeometry(geometry);
                    }}
                  />
                </div>
              )}
            </div>

            <FieldDataDrawer ref={drawerRef} title={w.fieldDataTitle(fmt(visibleFeatures.length))} onOpenChange={setTableOpen}>
              <FeatureTable
                layers={editable.layers ?? []}
                selectedLayerShape={zoomedLayerShape}
                onZoomToLayerShape={(layerId, position) => void zoomToLayerShape(layerId, position)}
                // Export follows the filter: the whole project, one zone, one
                // ward, or one block.
                scope={{
                  projectId,
                  zoneId: filter.zoneId ?? undefined,
                  wardId: filter.wardId ?? undefined,
                  blockId: filter.blockId ?? undefined,
                }}
                hierarchy={hierarchy}
                features={visibleFeatures}
                onChanged={refreshFeatures}
                selectedId={zoomedFeatureId}
                onZoomTo={(feature) => {
                  if (!feature.geometry) return;
                  const geometry = feature.geometry;
                  setZoomedFeatureId(feature.id);
                  afterReveal(() => mapRef.current?.zoomToGeometry(geometry));
                }}
              />
            </FieldDataDrawer>
          </div>
        </div>
      </div>

      {isStaff && (
        <ManageUploadsDialog open={uploadsOpen} onOpenChange={setUploadsOpen}>
          <div className="h-[24rem] min-h-0">
            <RasterPanel
              scope={{ projectId }}
              project={project ?? undefined}
              rasters={rasters}
              onChanged={refreshRasters}
              onZoomTo={(bounds) => {
                setUploadsOpen(false);
                mapRef.current?.flyToBounds(bounds);
              }}
            />
          </div>
          <div className="h-[24rem] min-h-0">
            <PropertyImportPanel
              projectId={projectId}
              refreshKey={propertyKey}
              onImported={() => {
                refreshFeatures();
                setPropertyKey((k) => k + 1);
              }}
            />
          </div>
          <div className="h-[24rem] min-h-0 md:col-span-2 xl:col-span-1">
            <EditableLayersPanel
              state={editable}
              onZoomToLayer={(bbox) => {
                setUploadsOpen(false);
                mapRef.current?.flyToBounds(bbox);
              }}
            />
          </div>
        </ManageUploadsDialog>
      )}
    </AppShell>
  );
}
